import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import AddIcon from '@mui/icons-material/Add';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import FlashOnOutlinedIcon from '@mui/icons-material/FlashOnOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import {
  Timestamp,
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../AuthContext';

// ─── Types ──────────────────────────────────────────────────────────────────

type Lead = {
  id: string;
  name?: string;
  phone?: string;
  phone10?: string;
  status?: string;
  visitCount?: number;
  siteVisitCount?: number;
  projectId?: string;
  projectName?: string;
  source?: string;
  enquiryType?: string;
  lastActivityAt?: any;
};

type TemplateVar = { key: string; source?: string; label?: string; defaultValue?: string };
type TemplateBtnVar = { index: string; key: string; source?: string; label?: string; defaultValue?: string };

type WaTemplate = {
  id: string;
  name?: string;
  bodyText?: string;
  language?: string;
  templateLanguage?: string;
  status?: string;
  headerFormat?: string;
  headerImageUrl?: string;
  headerDocumentUrl?: string;
  variables?: TemplateVar[];
  buttonVariables?: TemplateBtnVar[];
};

type WaSender = {
  id: string;
  name?: string;
  label?: string;
  displayPhoneNumber?: string;
  active?: boolean;
};

function senderLabel(s: WaSender): string {
  return s.name || s.label || s.displayPhoneNumber || s.id;
}

type LeadStatus = { id: string; label?: string };
type Project = { id: string; name?: string; logoUrl?: string; brochureUrl?: string };

type CampaignFilters = {
  statuses: string[];
  excludeStatuses: string[];
  visitCountMin: number | null;
  visitCountMax: number | null;
  daysSinceActivity: number | null;
  projectId: string | null;
};

type CampaignRecord = {
  id: string;
  name?: string;
  status?: string;
  audienceCount?: number;
  sentCount?: number;
  failedCount?: number;
  messageType?: string;
  templateName?: string;
  previewText?: string;
  createdAt?: any;
  createdBy?: string;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const GOLD = '#B8860B';
const GOLD_LIGHT = '#FFF8E1';
const STEPS = ['Audience', 'Message', 'Review & Send'];

const EMPTY_FILTERS: CampaignFilters = {
  statuses: [],
  excludeStatuses: [],
  visitCountMin: null,
  visitCountMax: null,
  daysSinceActivity: null,
  projectId: null,
};

type QuickSegment = {
  id: string;
  label: string;
  description: string;
  filters: Partial<CampaignFilters>;
};

const CLOSED_STATUSES = [
  'lost', 'not interested', 'closed', 'cancelled', 'completed', 'won',
];

const QUICK_SEGMENTS: QuickSegment[] = [
  {
    id: 'active-visitors',
    label: 'Active + Visited',
    description: 'Non-closed leads with at least 1 site visit',
    filters: { visitCountMin: 1, excludeStatuses: CLOSED_STATUSES },
  },
  {
    id: 'never-visited',
    label: 'Never Visited',
    description: 'Active leads, 0 site visits, silent 14+ days',
    filters: { visitCountMax: 0, daysSinceActivity: 14, excludeStatuses: CLOSED_STATUSES },
  },
  {
    id: 'cold-leads',
    label: 'Cold Leads',
    description: 'Any lead, no activity for 30+ days',
    filters: { daysSinceActivity: 30, excludeStatuses: CLOSED_STATUSES },
  },
  {
    id: 'post-visit',
    label: 'Post-Visit',
    description: 'Visited at least once, no follow-up in 7+ days',
    filters: { visitCountMin: 1, daysSinceActivity: 7, excludeStatuses: CLOSED_STATUSES },
  },
  {
    id: 'hot-leads',
    label: 'Hot Leads',
    description: '2+ site visits — highly interested',
    filters: { visitCountMin: 2, excludeStatuses: CLOSED_STATUSES },
  },
  {
    id: 'win-back',
    label: 'Win-back',
    description: 'Lost / Not Interested, silent 60+ days',
    filters: {
      statuses: ['lost', 'not interested', 'closed', 'cancelled', 'completed'],
      daysSinceActivity: 60,
    },
  },
];

const SEND_DELAY_MS = 600; // between sends to respect WA rate limits

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toDate(v: any): Date | null {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (v instanceof Date) return v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function getVisits(lead: Lead): number {
  return (lead as any).siteVisitCount ?? lead.visitCount ?? 0;
}

function leadPhone(lead: Lead): string | null {
  if (lead.phone) return lead.phone.replace(/^\+/, '');
  if (lead.phone10) return `91${lead.phone10}`;
  return null;
}

function applyFilters(
  leads: Lead[],
  filters: CampaignFilters,
  statusLabelMap: Record<string, string>,
): Lead[] {
  const now = Date.now();
  return leads.filter((lead) => {
    const phone = leadPhone(lead);
    if (!phone) return false; // can't message without phone

    // Resolve status: stored as Firestore doc ID — map to lowercase label
    const statusId = lead.status || '';
    const statusLabel = (statusLabelMap[statusId] || statusId).toLowerCase().trim();

    // Include filter: values are Firestore doc IDs (from dropdown)
    if (filters.statuses.length > 0) {
      if (!filters.statuses.includes(statusId)) return false;
    }

    // Exclude filter: values are lowercase label strings (from quick segments)
    if ((filters.excludeStatuses || []).length > 0) {
      if (filters.excludeStatuses.some((s) => statusLabel === s.toLowerCase())) {
        return false;
      }
    }

    const visits = getVisits(lead);
    if (filters.visitCountMin !== null && visits < filters.visitCountMin) return false;
    if (filters.visitCountMax !== null && visits > filters.visitCountMax) return false;

    if (filters.daysSinceActivity !== null) {
      const lastAct = toDate(lead.lastActivityAt);
      if (lastAct) {
        const diffDays = (now - lastAct.getTime()) / 86400000;
        if (diffDays < filters.daysSinceActivity) return false;
      }
    }

    if (filters.projectId && lead.projectId !== filters.projectId) return false;

    return true;
  });
}

function fmtDate(v: any): string {
  const d = toDate(v);
  if (!d) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const LOGO_TOKENS = ['{{project_logo_url}}', '{{voucher_or_project_logo_url}}'];
const BROCHURE_TOKEN = '{{project_brochure_url}}';

// Sources auto-resolved per lead — user can override at campaign level
const AUTO_SOURCES = ['customer_name', 'project_name'];

function resolveBodyVar(
  v: TemplateVar,
  lead: { name?: string; projectName?: string } | null,
  campaignVars: Record<string, string>,
): string {
  // Campaign-level override wins (if user set a static value)
  const override = campaignVars[v.key];
  if (override !== undefined && override !== '') return override;
  // Per-lead auto-resolution
  if (v.source === 'customer_name') return lead?.name || v.defaultValue || 'Customer';
  if (v.source === 'project_name') return lead?.projectName || v.defaultValue || '';
  // Fallback to defaultValue
  return v.defaultValue || '—';
}

function buildTemplateComponents(
  template: WaTemplate,
  lead: { name?: string; projectName?: string } | null,
  projectsById: Record<string, Project>,
  projectId?: string | null,
  campaignVars?: Record<string, string>,
  campaignBtnVars?: Record<string, string>,
): Record<string, any>[] {
  const components: Record<string, any>[] = [];
  const vars = campaignVars ?? {};
  const btnVars = campaignBtnVars ?? {};
  const headerFormat = (template.headerFormat || '').toUpperCase();
  const proj = (projectId ? projectsById[projectId] : null) ?? Object.values(projectsById)[0] ?? null;

  if (headerFormat === 'IMAGE') {
    const raw = template.headerImageUrl || '';
    const imageUrl = LOGO_TOKENS.includes(raw) ? (proj?.logoUrl || '') : raw;
    if (imageUrl) {
      components.push({ type: 'header', parameters: [{ type: 'image', image: { link: imageUrl } }] });
    }
  } else if (headerFormat === 'DOCUMENT') {
    const raw = template.headerDocumentUrl || '';
    const docUrl = raw === BROCHURE_TOKEN ? (proj?.brochureUrl || '') : raw;
    if (docUrl) {
      components.push({ type: 'header', parameters: [{ type: 'document', document: { link: docUrl } }] });
    }
  }

  if ((template.variables || []).length > 0) {
    components.push({
      type: 'body',
      parameters: (template.variables || []).map((v) => ({
        type: 'text',
        text: resolveBodyVar(v, lead, vars),
      })),
    });
  }

  // Button variables (e.g. URL suffix)
  for (const btn of (template.buttonVariables || [])) {
    const stateKey = `${btn.index}_${btn.key}`;
    const value = (btnVars[stateKey] ?? btn.defaultValue ?? '').trim();
    if (value) {
      components.push({
        type: 'button',
        sub_type: 'url',
        index: String(btn.index ?? '0'),
        parameters: [{ type: 'text', text: value }],
      });
    }
  }

  return components;
}

function resolveTemplateText(template: WaTemplate, lead: Lead): string {
  let text = String(template.bodyText || '');
  text = text.replace(/\{\{customer_name\}\}/gi, lead.name || 'Customer');
  text = text.replace(/\{\{lead_name\}\}/gi, lead.name || 'Customer');
  text = text.replace(/\{\{project_name\}\}/gi, lead.projectName || '');
  text = text.replace(/\{\{\d+\}\}/g, '___');
  return text;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CampaignsPage() {
  const { currentUser } = useAuth();

  // Data
  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [statuses, setStatuses] = useState<LeadStatus[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [senders, setSenders] = useState<WaSender[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignRecord[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // Dialog / stepper
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Step 1 — Audience
  const [campaignName, setCampaignName] = useState('');
  const [activeSegment, setActiveSegment] = useState<string | null>(null);
  const [filters, setFilters] = useState<CampaignFilters>(EMPTY_FILTERS);
  const [customFilterOpen, setCustomFilterOpen] = useState(false);

  // Step 2 — Message
  const [messageType, setMessageType] = useState<'template' | 'text'>('template');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [customText, setCustomText] = useState('');
  const [selectedSenderId, setSelectedSenderId] = useState('');
  // Campaign-level variable overrides (key → static value; empty = auto-resolve per lead)
  const [campaignVars, setCampaignVars] = useState<Record<string, string>>({});
  // Button variable overrides (index_key → value)
  const [campaignBtnVars, setCampaignBtnVars] = useState<Record<string, string>>({});

  // Test send
  const [testPhone, setTestPhone] = useState('');
  const [testState, setTestState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [testError, setTestError] = useState<string | null>(null);
  const [testWaMessageId, setTestWaMessageId] = useState<string | null>(null);

  // Step 3 — Send
  const [sendState, setSendState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [sendProgress, setSendProgress] = useState({ total: 0, sent: 0, failed: 0 });
  const [sendError, setSendError] = useState<string | null>(null);
  const abortRef = useRef(false);

  // Derived
  const statusLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of statuses) map[s.id] = s.label || s.id;
    return map;
  }, [statuses]);

  const matchedLeads = useMemo(
    () => applyFilters(allLeads, filters, statusLabelMap),
    [allLeads, filters, statusLabelMap],
  );
  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) ?? null,
    [templates, selectedTemplateId]
  );

  // When template changes, seed campaignVars / campaignBtnVars from defaultValues
  useEffect(() => {
    if (!selectedTemplate) { setCampaignVars({}); setCampaignBtnVars({}); return; }
    const vars: Record<string, string> = {};
    for (const v of (selectedTemplate.variables || [])) vars[v.key] = v.defaultValue ?? '';
    setCampaignVars(vars);
    const btnVars: Record<string, string> = {};
    for (const v of (selectedTemplate.buttonVariables || [])) {
      btnVars[`${v.index}_${v.key}`] = v.defaultValue ?? '';
    }
    setCampaignBtnVars(btnVars);
  }, [selectedTemplateId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingData(true);
      try {
        const [leadsSnap, statusSnap, projSnap, templateSnap, senderSnap, campaignSnap] =
          await Promise.all([
            getDocs(query(collection(db, 'leads'), orderBy('lastActivityAt', 'desc'), limit(2000))),
            getDocs(collection(db, 'leadStatuses')),
            getDocs(collection(db, 'projects')),
            getDocs(query(collection(db, 'whatsappTemplates'), orderBy('updatedAt', 'desc'))),
            getDocs(query(collection(db, 'whatsappSenders'), where('active', '==', true))),
            getDocs(query(collection(db, 'whatsappCampaigns'), orderBy('createdAt', 'desc'), limit(50))),
          ]);
        if (cancelled) return;

        setAllLeads(leadsSnap.docs.map((d) => ({ id: d.id, ...d.data() } as Lead)));
        setStatuses(statusSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setProjects(projSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setTemplates(templateSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setSenders(senderSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setCampaigns(campaignSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));

        // Default sender
        const defaultSender = senderSnap.docs[0];
        if (defaultSender) setSelectedSenderId(defaultSender.id);
      } catch (err) {
        console.error('CampaignsPage load error', err);
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // ── Dialog open/close ──────────────────────────────────────────────────────
  const openDialog = useCallback(() => {
    setStep(0);
    setCampaignName('');
    setActiveSegment(null);
    setFilters(EMPTY_FILTERS);
    setCustomFilterOpen(false);
    setMessageType('template');
    setSelectedTemplateId('');
    setCustomText('');
    setCampaignVars({});
    setCampaignBtnVars({});
    setSendState('idle');
    setSendProgress({ total: 0, sent: 0, failed: 0 });
    setSendError(null);
    setTestPhone('');
    setTestState('idle');
    setTestError(null);
    setTestWaMessageId(null);
    abortRef.current = false;
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    if (sendState === 'sending') return; // prevent accidental close
    setDialogOpen(false);
  }, [sendState]);

  // ── Quick segment selection ────────────────────────────────────────────────
  const applySegment = useCallback((seg: QuickSegment) => {
    setActiveSegment(seg.id);
    setFilters({ ...EMPTY_FILTERS, ...seg.filters } as CampaignFilters);
    setCampaignName(seg.label);
    setCustomFilterOpen(false);
  }, []);

  // ── Test send ─────────────────────────────────────────────────────────────
  const handleTestSend = useCallback(async () => {
    const phone = testPhone.replace(/\D/g, '');
    if (!phone) return;
    const template = selectedTemplate;
    const text = customText.trim();
    if (messageType === 'template' && !template) return;
    if (messageType === 'text' && !text) return;

    setTestState('sending');
    setTestError(null);
    const sendFn = httpsCallable(functions, 'whatsappSendMessage');
    try {
      const payload: Record<string, any> = {
        senderId: selectedSenderId || undefined,
        to: phone,
      };
      if (messageType === 'template' && template) {
        // Look up the test number in allLeads to get real name/project
        const matchedLead = allLeads.find((l) => {
          const lp = leadPhone(l);
          return lp && (lp === phone || lp.endsWith(phone) || phone.endsWith(lp));
        });
        const testLead = {
          name: matchedLead?.name || 'Test Lead',
          projectName: matchedLead?.projectName || projects[0]?.name || 'Swarna',
        };
        const projectsById = Object.fromEntries(projects.map((p) => [p.id, p]));
        const testProjectId = matchedLead?.projectId || projects[0]?.id;
        payload.templateName = String(template.name || '').trim();
        payload.templateLanguage = String(template.templateLanguage || template.language || 'en').trim();
        payload.resolvedText = resolveTemplateText(template, { id: 'test', ...testLead });
        payload.templateComponents = buildTemplateComponents(template, testLead, projectsById, testProjectId, campaignVars, campaignBtnVars);
      } else {
        payload.text = text;
      }
      const result = await sendFn(payload);
      setTestWaMessageId((result?.data as any)?.waMessageId ?? null);
      setTestState('sent');
    } catch (err: any) {
      setTestError(err?.message || 'Test send failed');
      setTestState('error');
    }
  }, [testPhone, messageType, selectedTemplate, customText, selectedSenderId, projects, allLeads, campaignVars, campaignBtnVars]);

  // ── Send campaign ──────────────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    const leads = matchedLeads;
    if (!leads.length) return;

    const msgType = messageType;
    const template = selectedTemplate;
    const text = customText.trim();
    if (msgType === 'template' && !template) return;
    if (msgType === 'text' && !text) return;

    abortRef.current = false;
    setSendState('sending');
    setSendProgress({ total: leads.length, sent: 0, failed: 0 });
    setSendError(null);

    const sendFn = httpsCallable(functions, 'whatsappSendMessage');
    let sent = 0;
    let failed = 0;

    for (const lead of leads) {
      if (abortRef.current) break;
      const phone = leadPhone(lead);
      if (!phone) { failed++; setSendProgress({ total: leads.length, sent, failed }); continue; }

      try {
        const payload: Record<string, any> = {
          senderId: selectedSenderId || undefined,
          to: phone,
          leadId: lead.id,
          projectId: lead.projectId || undefined,
        };

        if (msgType === 'template' && template) {
          const projectsById = Object.fromEntries(projects.map((p) => [p.id, p]));
          const resolvedText = resolveTemplateText(template, lead);
          payload.templateName = String(template.name || '').trim();
          payload.templateLanguage = String(template.templateLanguage || template.language || 'en').trim();
          payload.resolvedText = resolvedText;
          payload.templateComponents = buildTemplateComponents(template, lead, projectsById, lead.projectId, campaignVars, campaignBtnVars);
        } else {
          payload.text = text;
        }

        await sendFn(payload);
        sent++;
      } catch (err) {
        console.error('Send failed for lead', lead.id, err);
        failed++;
      }

      setSendProgress({ total: leads.length, sent, failed });
      await sleep(SEND_DELAY_MS);
    }

    // Save campaign record
    try {
      const previewText = msgType === 'template' && template
        ? String(template.bodyText || '').slice(0, 120)
        : text.slice(0, 120);

      const campRef = await addDoc(collection(db, 'whatsappCampaigns'), {
        name: campaignName || (msgType === 'template' ? template?.name : 'Custom Text') || 'Campaign',
        status: failed > 0 && sent === 0 ? 'failed' : failed > 0 ? 'partial' : 'completed',
        audienceCount: leads.length,
        sentCount: sent,
        failedCount: failed,
        messageType: msgType,
        templateName: msgType === 'template' ? (template?.name || null) : null,
        previewText,
        filters,
        activeSegment,
        senderId: selectedSenderId || null,
        createdAt: Timestamp.now(),
        createdBy: currentUser?.email || null,
      });

      setCampaigns((prev): CampaignRecord[] => [{
        id: campRef.id,
        name: campaignName || template?.name || 'Campaign',
        status: failed > 0 && sent === 0 ? 'failed' : failed > 0 ? 'partial' : 'completed',
        audienceCount: leads.length,
        sentCount: sent,
        failedCount: failed,
        messageType: msgType,
        templateName: template?.name,
        previewText,
        createdAt: Timestamp.now(),
        createdBy: currentUser?.email || undefined,
      }, ...prev]);
    } catch (err) {
      console.error('Failed to save campaign record', err);
    }

    setSendState('done');
  }, [matchedLeads, messageType, selectedTemplate, customText, selectedSenderId, campaignName, filters, activeSegment, currentUser]);

  // ─── Render ──────────────────────────────────────────────────────────────

  const canNext = useMemo(() => {
    if (step === 0) return matchedLeads.length > 0;
    if (step === 1) {
      if (messageType === 'template') return !!selectedTemplateId;
      return customText.trim().length > 0;
    }
    return true;
  }, [step, matchedLeads.length, messageType, selectedTemplateId, customText]);

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 960, mx: 'auto' }}>
      {/* Page header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <CampaignOutlinedIcon sx={{ color: GOLD, fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              WhatsApp Campaigns
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Send bulk messages to segmented leads
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openDialog}
          sx={{
            bgcolor: GOLD,
            color: '#fff',
            fontWeight: 600,
            '&:hover': { bgcolor: '#9a7209' },
          }}
        >
          New Campaign
        </Button>
      </Stack>

      {/* Stats row */}
      <Stack direction="row" gap={2} mb={3} flexWrap="wrap">
        {[
          { label: 'Total Campaigns', value: campaigns.length },
          { label: 'Total Sent', value: campaigns.reduce((s, c) => s + (c.sentCount || 0), 0) },
          { label: 'Leads Available', value: allLeads.length },
        ].map((stat) => (
          <Paper
            key={stat.label}
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 2.5, py: 1.5, flex: '1 1 140px' }}
          >
            <Typography variant="h5" fontWeight={700} color={GOLD}>{stat.value}</Typography>
            <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
          </Paper>
        ))}
      </Stack>

      {/* Campaign history */}
      {campaigns.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            py: 8,
            textAlign: 'center',
          }}
        >
          <HistoryOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">No campaigns yet</Typography>
          <Typography variant="body2" color="text.disabled">
            Create your first campaign to reach leads in bulk
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openDialog}
            sx={{ mt: 2, borderColor: GOLD, color: GOLD, '&:hover': { borderColor: GOLD, bgcolor: GOLD_LIGHT } }}
          >
            New Campaign
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                {['Campaign', 'Message', 'Audience', 'Sent', 'Failed', 'Status', 'Date'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell sx={{ fontWeight: 600, maxWidth: 180 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{c.name || '—'}</Typography>
                    {c.previewText && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 160 }}>
                        {c.previewText}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={c.messageType === 'template' ? (c.templateName || 'Template') : 'Custom Text'}
                      icon={<WhatsAppIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <GroupOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="body2">{c.audienceCount ?? '—'}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
                      <Typography variant="body2">{c.sentCount ?? 0}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <ErrorOutlineIcon sx={{ fontSize: 14, color: c.failedCount ? 'error.main' : 'text.disabled' }} />
                      <Typography variant="body2" color={c.failedCount ? 'error.main' : 'text.secondary'}>
                        {c.failedCount ?? 0}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={c.status === 'completed' ? 'Done' : c.status === 'partial' ? 'Partial' : c.status === 'failed' ? 'Failed' : c.status || '—'}
                      sx={{
                        fontSize: 11,
                        bgcolor: c.status === 'completed' ? '#e8f5e9' : c.status === 'failed' ? '#ffebee' : '#fff8e1',
                        color: c.status === 'completed' ? '#2e7d32' : c.status === 'failed' ? '#c62828' : '#f57f17',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{fmtDate(c.createdAt)}</Typography>
                    {c.createdBy && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                        {String(c.createdBy).split('@')[0]}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ─── New Campaign Dialog ─────────────────────────────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ pb: '10px', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap={1}>
              <CampaignOutlinedIcon sx={{ color: GOLD }} />
              <Typography fontWeight={700}>New Campaign</Typography>
            </Stack>
            <IconButton onClick={closeDialog} disabled={sendState === 'sending'} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stepper activeStep={step} sx={{ mt: 2, mb: 0.5 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': { fontSize: 13 },
                    '& .MuiStepIcon-root.Mui-active': { color: GOLD },
                    '& .MuiStepIcon-root.Mui-completed': { color: GOLD },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogTitle>

        <DialogContent sx={{ pt: '20px' }}>
          {/* ── Step 0: Audience ───────────────────────────────────────────── */}
          {step === 0 && (
            <Stack gap={3}>
              <TextField
                label="Campaign Name"
                size="small"
                fullWidth
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g. Cold Leads Re-engagement — July"
              />

              {/* Quick segments */}
              <Box>
                <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                  <FlashOnOutlinedIcon sx={{ fontSize: 16, color: GOLD }} />
                  <Typography variant="subtitle2" fontWeight={600}>Quick Segments</Typography>
                </Stack>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {QUICK_SEGMENTS.map((seg) => (
                    <Tooltip key={seg.id} title={seg.description} arrow>
                      <Chip
                        label={seg.label}
                        clickable
                        onClick={() => applySegment(seg)}
                        sx={{
                          fontWeight: activeSegment === seg.id ? 700 : 400,
                          bgcolor: activeSegment === seg.id ? GOLD : undefined,
                          color: activeSegment === seg.id ? '#fff' : undefined,
                          borderWidth: activeSegment === seg.id ? 0 : 1,
                          border: '1px solid',
                          borderColor: activeSegment === seg.id ? 'transparent' : 'divider',
                          '&:hover': { bgcolor: activeSegment === seg.id ? '#9a7209' : GOLD_LIGHT },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>

              {/* Custom filters toggle */}
              <Box>
                <Button
                  size="small"
                  startIcon={<TuneOutlinedIcon />}
                  onClick={() => setCustomFilterOpen((v) => !v)}
                  sx={{ color: 'text.secondary', textTransform: 'none', mb: 1 }}
                >
                  {customFilterOpen ? 'Hide custom filters' : 'Custom filters'}
                </Button>

                {customFilterOpen && (
                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                    <Stack gap={2}>
                      {/* Statuses */}
                      <FormControl size="small" fullWidth>
                        <InputLabel>Lead Statuses</InputLabel>
                        <Select
                          multiple
                          value={filters.statuses}
                          label="Lead Statuses"
                          onChange={(e) => {
                            setActiveSegment(null);
                            setFilters((f) => ({ ...f, statuses: e.target.value as string[] }));
                          }}
                          renderValue={(selected) =>
                            (selected as string[])
                              .map((id) => statusLabelMap[id] || id)
                              .join(', ') || 'Any'
                          }
                        >
                          {statuses.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.label || s.id}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Visit count */}
                      <Stack direction="row" gap={2}>
                        <FormControl size="small" sx={{ flex: 1 }}>
                          <InputLabel>Min Visits</InputLabel>
                          <Select
                            value={filters.visitCountMin !== null ? String(filters.visitCountMin) : ''}
                            label="Min Visits"
                            onChange={(e) => {
                              setActiveSegment(null);
                              setFilters((f) => ({ ...f, visitCountMin: e.target.value === '' ? null : Number(e.target.value) }));
                            }}
                          >
                            <MenuItem value="">Any</MenuItem>
                            {[0, 1, 2, 3, 5].map((v) => <MenuItem key={v} value={String(v)}>{v}+</MenuItem>)}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ flex: 1 }}>
                          <InputLabel>Max Visits</InputLabel>
                          <Select
                            value={filters.visitCountMax !== null ? String(filters.visitCountMax) : ''}
                            label="Max Visits"
                            onChange={(e) => {
                              setActiveSegment(null);
                              setFilters((f) => ({ ...f, visitCountMax: e.target.value === '' ? null : Number(e.target.value) }));
                            }}
                          >
                            <MenuItem value="">Any</MenuItem>
                            {[0, 1, 2, 3, 5].map((v) => <MenuItem key={v} value={String(v)}>{v}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Stack>

                      {/* Days since activity */}
                      <FormControl size="small" fullWidth>
                        <InputLabel>Silent for at least</InputLabel>
                        <Select
                          value={filters.daysSinceActivity !== null ? String(filters.daysSinceActivity) : ''}
                          label="Silent for at least"
                          onChange={(e) => {
                            setActiveSegment(null);
                            setFilters((f) => ({ ...f, daysSinceActivity: e.target.value === '' ? null : Number(e.target.value) }));
                          }}
                        >
                          <MenuItem value="">Any time</MenuItem>
                          {[7, 14, 30, 60, 90].map((d) => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
                        </Select>
                      </FormControl>

                      {/* Project */}
                      <FormControl size="small" fullWidth>
                        <InputLabel>Project</InputLabel>
                        <Select
                          value={filters.projectId ?? ''}
                          label="Project"
                          onChange={(e) => {
                            setActiveSegment(null);
                            setFilters((f) => ({ ...f, projectId: e.target.value || null }));
                          }}
                        >
                          <MenuItem value="">All Projects</MenuItem>
                          {projects.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{(p as any).name || p.id}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Button
                        size="small"
                        onClick={() => { setFilters(EMPTY_FILTERS); setActiveSegment(null); }}
                        sx={{ alignSelf: 'flex-start', color: 'text.secondary', textTransform: 'none' }}
                      >
                        Clear filters
                      </Button>
                    </Stack>
                  </Paper>
                )}
              </Box>

              {/* Live count */}
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: matchedLeads.length > 0 ? GOLD : 'divider',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: matchedLeads.length > 0 ? GOLD_LIGHT : '#fafafa',
                  textAlign: 'center',
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                  <GroupOutlinedIcon sx={{ color: matchedLeads.length > 0 ? GOLD : 'text.disabled' }} />
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color={matchedLeads.length > 0 ? GOLD : 'text.disabled'}
                  >
                    {matchedLeads.length}
                  </Typography>
                  <Typography color={matchedLeads.length > 0 ? 'text.primary' : 'text.disabled'}>
                    leads match
                  </Typography>
                </Stack>
                {matchedLeads.length === 0 && (
                  <Typography variant="caption" color="text.disabled">
                    Adjust filters or select a quick segment
                  </Typography>
                )}
                {matchedLeads.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Each lead will receive one message
                  </Typography>
                )}
              </Paper>
            </Stack>
          )}

          {/* ── Step 1: Message ────────────────────────────────────────────── */}
          {step === 1 && (
            <Stack gap={3}>
              {/* Sender */}
              <FormControl size="small" fullWidth>
                <InputLabel>WhatsApp Sender</InputLabel>
                <Select
                  value={selectedSenderId}
                  label="WhatsApp Sender"
                  onChange={(e) => setSelectedSenderId(e.target.value)}
                  renderValue={(id) => {
                    const s = senders.find((x) => x.id === id);
                    return s ? senderLabel(s) : String(id);
                  }}
                >
                  {senders.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <WhatsAppIcon sx={{ fontSize: 16, color: '#25d366' }} />
                        {senderLabel(s)}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Message type toggle */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>Message Type</Typography>
                <ToggleButtonGroup
                  exclusive
                  value={messageType}
                  onChange={(_, v) => { if (v) setMessageType(v); }}
                  size="small"
                >
                  <ToggleButton value="template" sx={{ textTransform: 'none', fontSize: 13 }}>
                    Approved Template
                  </ToggleButton>
                  <ToggleButton value="text" sx={{ textTransform: 'none', fontSize: 13 }}>
                    Custom Text
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {messageType === 'template'
                    ? 'Templates work for any lead — even those who haven\'t messaged in 24hrs'
                    : 'Custom text only works within 24hrs of their last WhatsApp reply'}
                </Typography>
              </Box>

              {messageType === 'template' ? (
                <Stack gap={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Select Template</InputLabel>
                    <Select
                      value={selectedTemplateId}
                      label="Select Template"
                      onChange={(e) => setSelectedTemplateId(e.target.value)}
                    >
                      {templates.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          <Stack>
                            <Typography variant="body2" fontWeight={500}>{t.name || t.id}</Typography>
                            {t.bodyText && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 400 }}>
                                {t.bodyText.slice(0, 80)}
                              </Typography>
                            )}
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* ── Variable picker ── */}
                  {selectedTemplate && ((selectedTemplate.variables || []).length > 0 || (selectedTemplate.buttonVariables || []).length > 0) && (
                    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} mb={0.5}>Template Variables</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                        Auto-resolved variables use each lead's real data. Set a static value to send the same text to everyone.
                      </Typography>
                      <Stack gap={1.5}>
                        {(selectedTemplate.variables || []).map((v) => {
                          const isAuto = AUTO_SOURCES.includes(v.source || '');
                          return (
                            <Stack key={v.key} direction="row" alignItems="center" gap={1.5}>
                              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                                {v.label || v.key}
                              </Typography>
                              {isAuto && (
                                <Chip
                                  size="small"
                                  label={`Auto: ${v.source}`}
                                  sx={{ fontSize: 11, bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' }}
                                />
                              )}
                              <TextField
                                size="small"
                                placeholder={isAuto ? `Leave blank to use lead's ${v.source}` : (v.defaultValue || '')}
                                value={campaignVars[v.key] ?? ''}
                                onChange={(e) => setCampaignVars((prev) => ({ ...prev, [v.key]: e.target.value }))}
                                sx={{ flex: 1 }}
                                inputProps={{ style: { fontSize: 13 } }}
                              />
                            </Stack>
                          );
                        })}
                        {(selectedTemplate.buttonVariables || []).map((btn) => {
                          const stateKey = `${btn.index}_${btn.key}`;
                          return (
                            <Stack key={stateKey} direction="row" alignItems="center" gap={1.5}>
                              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                                {btn.label || btn.key} <Typography component="span" variant="caption">(Button {btn.index})</Typography>
                              </Typography>
                              <Chip size="small" label="URL param" sx={{ fontSize: 11, bgcolor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' }} />
                              <TextField
                                size="small"
                                placeholder={btn.defaultValue || 'Required'}
                                value={campaignBtnVars[stateKey] ?? ''}
                                onChange={(e) => setCampaignBtnVars((prev) => ({ ...prev, [stateKey]: e.target.value }))}
                                sx={{ flex: 1 }}
                                inputProps={{ style: { fontSize: 13 } }}
                                required
                              />
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Paper>
                  )}

                  {selectedTemplate && (
                    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: '#f9f9f9' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                        PREVIEW (sample lead)
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: '#dcf8c6',
                          borderRadius: '12px 12px 2px 12px',
                          p: 1.5,
                          maxWidth: 320,
                          ml: 'auto',
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                          {resolveTemplateText(selectedTemplate, {
                            id: 'preview',
                            name: 'Rahul Shah',
                            projectName: 'Swarna Avsar',
                          })}
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Stack>
              ) : (
                <Stack gap={2}>
                  <TextField
                    label="Message"
                    multiline
                    rows={4}
                    fullWidth
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    placeholder="Type your WhatsApp message..."
                    helperText={`${customText.length} characters`}
                  />
                  {customText && (
                    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: '#f9f9f9' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                        PREVIEW
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: '#dcf8c6',
                          borderRadius: '12px 12px 2px 12px',
                          p: 1.5,
                          maxWidth: 320,
                          ml: 'auto',
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                          {customText}
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Stack>
              )}
            </Stack>
          )}

          {/* ── Step 2: Review & Send ──────────────────────────────────────── */}
          {step === 2 && (
            <Stack gap={3}>
              {sendState === 'idle' && (
                <>
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    You are about to send a WhatsApp message to <strong>{matchedLeads.length} leads</strong>.
                    This cannot be undone. Review carefully before sending.
                  </Alert>

                  {/* Summary */}
                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={2}>Campaign Summary</Typography>
                    <Stack gap={1.5}>
                      {[
                        { label: 'Campaign Name', value: campaignName || '(untitled)' },
                        { label: 'Audience', value: `${matchedLeads.length} leads` },
                        {
                          label: 'Segment',
                          value: activeSegment
                            ? QUICK_SEGMENTS.find((s) => s.id === activeSegment)?.label || activeSegment
                            : 'Custom filters',
                        },
                        {
                          label: 'Message',
                          value: messageType === 'template'
                            ? `Template: ${selectedTemplate?.name || '—'}`
                            : `Custom: "${customText.slice(0, 50)}${customText.length > 50 ? '…' : ''}"`,
                        },
                        {
                          label: 'Sender',
                          value: (() => {
                            const s = senders.find((x) => x.id === selectedSenderId);
                            return s ? senderLabel(s) : (selectedSenderId || 'Default');
                          })(),
                        },
                      ].map(({ label, value }) => (
                        <Stack key={label} direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>{label}</Typography>
                          <Typography variant="body2" fontWeight={500} textAlign="right" sx={{ maxWidth: 280 }}>{value}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>

                  {/* Test send */}
                  <Paper
                    elevation={0}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}
                  >
                    <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                      🧪 Send Test Message
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                      Send to a single number to verify the message looks correct before the full campaign.
                    </Typography>
                    <Stack direction="row" gap={1} alignItems="flex-start">
                      <TextField
                        size="small"
                        placeholder="91XXXXXXXXXX"
                        value={testPhone}
                        onChange={(e) => {
                          setTestPhone(e.target.value);
                          setTestState('idle');
                          setTestError(null);
                        }}
                        sx={{ flex: 1 }}
                        inputProps={{ inputMode: 'numeric' }}
                        helperText="Include country code, no + or spaces"
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!testPhone.replace(/\D/g, '') || testState === 'sending'}
                        onClick={handleTestSend}
                        startIcon={
                          testState === 'sending'
                            ? <CircularProgress size={14} sx={{ color: GOLD }} />
                            : <SendOutlinedIcon />
                        }
                        sx={{
                          borderColor: GOLD,
                          color: GOLD,
                          mt: 0.25,
                          whiteSpace: 'nowrap',
                          '&:hover': { borderColor: GOLD, bgcolor: GOLD_LIGHT },
                        }}
                      >
                        {testState === 'sending' ? 'Sending…' : 'Send Test'}
                      </Button>
                    </Stack>
                    {testState === 'sent' && (
                      <Alert severity={testWaMessageId ? 'success' : 'warning'} sx={{ mt: 1, py: 0.5, fontSize: 12 }}>
                        {testWaMessageId
                          ? <>Accepted by WhatsApp ✓ — <code style={{ fontSize: 11 }}>{testWaMessageId}</code><br />If not received: verify the template is <strong>APPROVED</strong> in Meta Business Manager and the language code matches.</>
                          : <>Request accepted but no message ID returned — check Firebase Logs and verify template status in Meta Business Manager.</>
                        }
                      </Alert>
                    )}
                    {testState === 'error' && (
                      <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
                        {testError || 'Test send failed. Check sender config.'}
                      </Alert>
                    )}
                  </Paper>

                  {/* Sample leads */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                      SAMPLE RECIPIENTS ({Math.min(5, matchedLeads.length)} of {matchedLeads.length})
                    </Typography>
                    <Stack gap={0.5}>
                      {matchedLeads.slice(0, 5).map((lead) => (
                        <Stack
                          key={lead.id}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ p: 1, borderRadius: 1, bgcolor: '#fafafa' }}
                        >
                          <Typography variant="body2">{lead.name || '(no name)'}</Typography>
                          <Stack direction="row" gap={1} alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                              {lead.phone || (lead.phone10 ? `+91 ${lead.phone10}` : '—')}
                            </Typography>
                            <Chip
                              size="small"
                              label={statusLabelMap[lead.status || ''] || lead.status || '—'}
                              sx={{ fontSize: 10, height: 18 }}
                            />
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}

              {/* Sending progress */}
              {(sendState === 'sending' || sendState === 'done') && (
                <Stack gap={3} alignItems="center" py={2}>
                  {sendState === 'sending' ? (
                    <>
                      <CircularProgress sx={{ color: GOLD }} size={48} />
                      <Typography variant="h6" fontWeight={600}>Sending messages…</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please keep this window open. Do not close or refresh.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CheckCircleOutlineIcon sx={{ fontSize: 56, color: sendProgress.failed === sendProgress.total ? 'error.main' : 'success.main' }} />
                      <Typography variant="h6" fontWeight={600}>
                        {sendProgress.failed === sendProgress.total ? 'Campaign failed' : 'Campaign complete!'}
                      </Typography>
                    </>
                  )}

                  <Box sx={{ width: '100%', maxWidth: 400 }}>
                    <LinearProgress
                      variant="determinate"
                      value={sendProgress.total > 0 ? ((sendProgress.sent + sendProgress.failed) / sendProgress.total) * 100 : 0}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: '#f0f0f0',
                        '& .MuiLinearProgress-bar': { bgcolor: GOLD, borderRadius: 5 },
                      }}
                    />
                    <Stack direction="row" justifyContent="space-between" mt={1}>
                      <Typography variant="caption" color="success.main">
                        ✓ {sendProgress.sent} sent
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sendProgress.sent + sendProgress.failed} / {sendProgress.total}
                      </Typography>
                      {sendProgress.failed > 0 && (
                        <Typography variant="caption" color="error.main">
                          ✗ {sendProgress.failed} failed
                        </Typography>
                      )}
                    </Stack>
                  </Box>

                  {sendError && (
                    <Alert severity="error" sx={{ width: '100%' }}>{sendError}</Alert>
                  )}
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>

        {/* Dialog footer */}
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {sendState === 'done' ? (
            <>
              <Box />
              <Button
                variant="contained"
                onClick={closeDialog}
                sx={{ bgcolor: GOLD, color: '#fff', '&:hover': { bgcolor: '#9a7209' } }}
              >
                Done
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  if (step === 0) closeDialog();
                  else setStep((s) => s - 1);
                }}
                disabled={sendState === 'sending'}
                sx={{ textTransform: 'none' }}
              >
                {step === 0 ? 'Cancel' : 'Back'}
              </Button>
              {step < 2 ? (
                <Button
                  variant="contained"
                  disabled={!canNext}
                  onClick={() => setStep((s) => s + 1)}
                  sx={{ bgcolor: GOLD, color: '#fff', '&:hover': { bgcolor: '#9a7209' } }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disabled={sendState === 'sending'}
                  startIcon={sendState === 'sending' ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendOutlinedIcon />}
                  onClick={handleSend}
                  sx={{ bgcolor: '#c62828', color: '#fff', '&:hover': { bgcolor: '#a31515' }, fontWeight: 700 }}
                >
                  {sendState === 'sending' ? 'Sending…' : `Send to ${matchedLeads.length} Leads`}
                </Button>
              )}
            </>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
